import React, { useState, useEffect, useRef } from 'react';

interface DebugInfo {
  buildVersion: string;
  environment: string;
  apiEndpoint: string;
  renderTime: number;
  localStorage: Record<string, string>;
  memoryUsage?: number;
  fpsRate?: number;
}

interface NetworkRequest {
  url: string;
  method: string;
  status: number;
  duration: number;
  timestamp: number;
}

interface ErrorLog {
  message: string;
  stack?: string;
  timestamp: number;
}

const DebugPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('info');
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({
    buildVersion: '0.1.0',
    environment: 'development',
    apiEndpoint: 'http://localhost:3001',
    renderTime: 0,
    localStorage: {},
  });

  const [features, setFeatures] = useState({
    showDevTools: false,
    slowNetwork: false,
    mockResponses: false,
  });

  const [networkRequests, setNetworkRequests] = useState<NetworkRequest[]>([]);
  const [errorLogs, setErrorLogs] = useState<ErrorLog[]>([]);
  const [newStorageKey, setNewStorageKey] = useState('');
  const [newStorageValue, setNewStorageValue] = useState('');

  // Refs for performance monitoring
  const fpsRef = useRef<number>(0);
  const frameCountRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(performance.now());
  const animationFrameId = useRef<number | null>(null);
  const originalFetch = useRef<typeof window.fetch>(window.fetch);

  const handleGlobalError = (event: ErrorEvent) => {
    setErrorLogs(prev => [
      {
        message: event.message,
        stack: event.error?.stack,
        timestamp: Date.now(),
      },
      ...prev.slice(0, 49), // Keep last 50 errors
    ]);
  };

  const setupErrorLogging = () => {
    window.addEventListener('error', handleGlobalError);
  };

  const startPerformanceMonitoring = () => {
    const updateFps = () => {
      frameCountRef.current += 1;
      const currentTime = performance.now();
      const elapsed = currentTime - lastTimeRef.current;

      if (elapsed >= 1000) {
        fpsRef.current = Math.round((frameCountRef.current * 1000) / elapsed);
        frameCountRef.current = 0;
        lastTimeRef.current = currentTime;

        // Update memory usage if available
        let memoryUsage = undefined;
        if (window.performance && 'memory' in performance) {
          const perfMemory = performance as { memory: { usedJSHeapSize: number } };
          memoryUsage = Math.round(perfMemory.memory.usedJSHeapSize / (1024 * 1024));
        }

        setDebugInfo(prev => ({
          ...prev,
          fpsRate: fpsRef.current,
          memoryUsage
        }));
      }

      animationFrameId.current = requestAnimationFrame(updateFps);
    };

    animationFrameId.current = requestAnimationFrame(updateFps);
  };

  const setupNetworkMonitoring = () => {
    // Store original fetch
    originalFetch.current = window.fetch;

    // Override fetch
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const start = performance.now();
      let url: string;

      if (typeof input === 'string') {
        url = input;
      } else if (input instanceof URL) {
        url = input.toString();
      } else {
        url = input.url;
      }

      const method = init?.method || 'GET';

      try {
        const response = await originalFetch.current(input, init);
        const duration = performance.now() - start;

        // Log the request
        setNetworkRequests(prev => [
          {
            url,
            method,
            status: response.status,
            duration,
            timestamp: Date.now(),
          },
          ...prev.slice(0, 49), // Keep last 50 requests
        ]);

        return response;
      } catch (error) {
        const duration = performance.now() - start;

        // Log the failed request
        setNetworkRequests(prev => [
          {
            url,
            method,
            status: 0,
            duration,
            timestamp: Date.now(),
          },
          ...prev.slice(0, 49),
        ]);

        throw error;
      }
    };
  };

  const restoreXhrAndFetch = () => {
    // Restore original fetch
    if (originalFetch.current) {
      window.fetch = originalFetch.current;
    }
  };

  useEffect(() => {
    // Get all localStorage items
    const localStorageItems: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        localStorageItems[key] = localStorage.getItem(key) || '';
      }
    }

    // Calculate render time (simplified example)
    const start = performance.now();
    setDebugInfo(prev => ({
      ...prev,
      renderTime: performance.now() - start,
      localStorage: localStorageItems,
    }));

    // Start performance monitoring
    startPerformanceMonitoring();

    // Set up network request monitoring
    setupNetworkMonitoring();

    // Set up error logging
    setupErrorLogging();

    return () => {
      // Clean up performance monitoring
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }

      // Clean up network monitoring
      restoreXhrAndFetch();

      // Clean up error logging
      window.removeEventListener('error', handleGlobalError);
    };
  }, [setupErrorLogging]);

  const toggleFeature = (featureName: keyof typeof features) => {
    setFeatures(prev => ({
      ...prev,
      [featureName]: !prev[featureName],
    }));
    // You could also store this in localStorage to persist across refreshes
    localStorage.setItem(`debug_${featureName}`, (!features[featureName]).toString());
  };

  const clearLocalStorage = () => {
    if (window.confirm('Are you sure you want to clear all localStorage data?')) {
      localStorage.clear();
      window.location.reload();
    }
  };

  const setLocalStorageItem = () => {
    if (newStorageKey) {
      localStorage.setItem(newStorageKey, newStorageValue);
      // Update the local state
      setDebugInfo(prev => ({
        ...prev,
        localStorage: {
          ...prev.localStorage,
          [newStorageKey]: newStorageValue
        }
      }));
      setNewStorageKey('');
      setNewStorageValue('');
    }
  };

  const removeLocalStorageItem = (key: string) => {
    localStorage.removeItem(key);
    // Update the local state
    const newLocalStorage = { ...debugInfo.localStorage };
    delete newLocalStorage[key];
    setDebugInfo(prev => ({
      ...prev,
      localStorage: newLocalStorage
    }));
  };

  const clearNetworkLogs = () => {
    setNetworkRequests([]);
  };

  const clearErrorLogs = () => {
    setErrorLogs([]);
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-medium mb-6">Debug Panel</h2>

      <div className="bg-[#2A2633] rounded-lg overflow-hidden">
        <div className="flex border-b border-white/10">
          <button
            className={`px-4 py-2 ${activeTab === 'info' ? 'bg-[#7F56D9] text-white' : 'hover:bg-white/5'}`}
            onClick={() => setActiveTab('info')}
          >
            System Info
          </button>
          <button
            className={`px-4 py-2 ${activeTab === 'features' ? 'bg-[#7F56D9] text-white' : 'hover:bg-white/5'}`}
            onClick={() => setActiveTab('features')}
          >
            Features
          </button>
          <button
            className={`px-4 py-2 ${activeTab === 'network' ? 'bg-[#7F56D9] text-white' : 'hover:bg-white/5'}`}
            onClick={() => setActiveTab('network')}
          >
            Network
          </button>
          <button
            className={`px-4 py-2 ${activeTab === 'storage' ? 'bg-[#7F56D9] text-white' : 'hover:bg-white/5'}`}
            onClick={() => setActiveTab('storage')}
          >
            Storage
          </button>
          <button
            className={`px-4 py-2 ${activeTab === 'errors' ? 'bg-[#7F56D9] text-white' : 'hover:bg-white/5'}`}
            onClick={() => setActiveTab('errors')}
          >
            Errors
          </button>
          <button
            className={`px-4 py-2 ${activeTab === 'actions' ? 'bg-[#7F56D9] text-white' : 'hover:bg-white/5'}`}
            onClick={() => setActiveTab('actions')}
          >
            Actions
          </button>
        </div>

        <div className="p-4">
          {activeTab === 'info' && (
            <div className="grid grid-cols-1 gap-2">
              <div className="flex justify-between">
                <span className="text-[var(--color-muted)]">Build Version:</span>
                <span>{debugInfo.buildVersion}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--color-muted)]">Environment:</span>
                <span>{debugInfo.environment}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--color-muted)]">API Endpoint:</span>
                <span>{debugInfo.apiEndpoint}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--color-muted)]">Render Time:</span>
                <span>{debugInfo.renderTime.toFixed(2)} ms</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--color-muted)]">FPS Rate:</span>
                <span>{debugInfo.fpsRate ?? 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--color-muted)]">Memory Usage:</span>
                <span>{debugInfo.memoryUsage ? `${debugInfo.memoryUsage} MB` : 'N/A'}</span>
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="space-y-4">
              {Object.entries(features).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-[var(--color-muted)]">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
                  <button
                    className={`relative inline-flex h-6 w-11 items-center rounded-full ${value ? 'bg-[#7F56D9]' : 'bg-gray-600'}`}
                    onClick={() => toggleFeature(key as keyof typeof features)}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${value ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'network' && (
            <div>
              <div className="flex justify-between mb-4">
                <h3 className="text-xl font-medium">Network Requests</h3>
                <button
                  onClick={clearNetworkLogs}
                  className="bg-red-600 hover:bg-red-700 text-white py-1 px-3 text-sm rounded-md transition-colors"
                >
                  Clear
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {networkRequests.length > 0 ? (
                  <div className="space-y-2">
                    {networkRequests.map((request, index) => (
                      <div key={index} className={`border-b border-white/10 pb-2 ${request.status >= 400 ? 'text-red-400' : ''}`}>
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{request.method} {request.url.split('/').pop()}</span>
                          <span>{formatTime(request.timestamp)}</span>
                        </div>
                        <div className="text-sm flex justify-between">
                          <span className="text-[var(--color-muted)]">{request.url}</span>
                        </div>
                        <div className="text-sm flex justify-between">
                          <span>Status: {request.status}</span>
                          <span>{request.duration.toFixed(2)} ms</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[var(--color-muted)]">No network requests logged</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'storage' && (
            <div>
              <div className="mb-4 pb-4 border-b border-white/10">
                <h3 className="text-xl font-medium mb-2">Add/Update Local Storage</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Key"
                    value={newStorageKey}
                    onChange={(e) => setNewStorageKey(e.target.value)}
                    className="flex-1 bg-[#1E1A25] p-2 rounded-md"
                  />
                  <input
                    type="text"
                    placeholder="Value"
                    value={newStorageValue}
                    onChange={(e) => setNewStorageValue(e.target.value)}
                    className="flex-1 bg-[#1E1A25] p-2 rounded-md"
                  />
                  <button
                    onClick={setLocalStorageItem}
                    disabled={!newStorageKey}
                    className="bg-[#7F56D9] hover:bg-[#6941C6] disabled:opacity-50 text-white py-2 px-4 rounded-md transition-colors"
                  >
                    Set
                  </button>
                </div>
              </div>
              <div className="flex justify-between mb-4">
                <h3 className="text-xl font-medium">Local Storage</h3>
                <button
                  onClick={clearLocalStorage}
                  className="bg-red-600 hover:bg-red-700 text-white py-1 px-3 text-sm rounded-md transition-colors"
                >
                  Clear All
                </button>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {Object.entries(debugInfo.localStorage).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(debugInfo.localStorage).map(([key, value]) => (
                      <div key={key} className="border-b border-white/10 pb-2 flex justify-between">
                        <div className="pr-4">
                          <div className="font-medium">{key}</div>
                          <div className="text-sm text-[var(--color-muted)] break-all">{value}</div>
                        </div>
                        <button
                          onClick={() => removeLocalStorageItem(key)}
                          className="h-6 text-red-400 hover:text-red-300"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[var(--color-muted)]">No items in localStorage</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'errors' && (
            <div>
              <div className="flex justify-between mb-4">
                <h3 className="text-xl font-medium">Error Logs</h3>
                <button
                  onClick={clearErrorLogs}
                  className="bg-red-600 hover:bg-red-700 text-white py-1 px-3 text-sm rounded-md transition-colors"
                >
                  Clear
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {errorLogs.length > 0 ? (
                  <div className="space-y-2">
                    {errorLogs.map((error, index) => (
                      <div key={index} className="border-b border-white/10 pb-2 text-red-400">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{error.message}</span>
                          <span>{formatTime(error.timestamp)}</span>
                        </div>
                        {error.stack && (
                          <pre className="text-xs mt-1 text-[var(--color-muted)] overflow-x-auto">
                            {error.stack}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[var(--color-muted)]">No errors logged</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'actions' && (
            <div>
              <h3 className="text-xl font-medium mb-4">Debug Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => console.log('Current state:', { debugInfo, features, networkRequests, errorLogs })}
                  className="bg-[#7F56D9] hover:bg-[#6941C6] text-white py-2 px-4 rounded-md transition-colors"
                >
                  Log State to Console
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-[#7F56D9] hover:bg-[#6941C6] text-white py-2 px-4 rounded-md transition-colors"
                >
                  Force Refresh
                </button>
                <button
                  onClick={() => {
                    try {
                      throw new Error('Test error from Debug Panel');
                    } catch (err) {
                      console.error(err);
                    }
                  }}
                  className="bg-[#7F56D9] hover:bg-[#6941C6] text-white py-2 px-4 rounded-md transition-colors"
                >
                  Generate Test Error
                </button>
                <button
                  onClick={() => {
                    fetch('https://jsonplaceholder.typicode.com/todos/1')
                      .then(response => response.json())
                      .then(json => console.log('Test fetch response:', json));
                  }}
                  className="bg-[#7F56D9] hover:bg-[#6941C6] text-white py-2 px-4 rounded-md transition-colors"
                >
                  Make Test Request
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DebugPanel;
